import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { shallow } from "zustand/shallow";
import useRegionManagementStore from "../../state/region-management-store";
import useRoutePlannerStore from "../../state/route-planner-store";
import { isUnassignedAddress } from "../utils/address-assignment";

export type ClickedAddress = {
  e: React.MouseEvent;
  address: string;
};

export const useAddressClickHandler = () => {
  const location = useLocation();
  const regionStore = useRegionManagementStore(
    (state) => ({
      findAddress: state.findAddress,
      setMapCentering: state.setMapCentering,
      setSelectedAddress: state.setSelectedAddress,
      selectedAddresses: state.selectedAddresses,
      unassignedOrders: state.unassignedOrders,
      setUnassignedOrders: state.setUnassignedOrders,
    }),
    shallow,
  );
  const routePlannerStore = useRoutePlannerStore(
    (state) => ({
      findAddress: state.findAddress,
      setMapCentering: state.setMapCentering,
      setSelectedAddress: state.setSelectedAddress,
      selectedAddresses: state.selectedAddresses,
    }),
    shallow,
  );

  // Determine which store to use based on current route
  const isRegionManagementPage =
    location.pathname.includes("region-management");

  const store = isRegionManagementPage ? regionStore : routePlannerStore;
  const { findAddress, setMapCentering, setSelectedAddress } = store;

  const setClickedAddress = useCallback(
    (data: ClickedAddress) => {
      const isShiftPressed = data.e.shiftKey;
      const fullAddress = findAddress(data.address);
      if (fullAddress !== undefined) {
        // If unassigned filter is active and user clicks on an assigned address,
        // turn off the filter so they can see it on the map
        if (isRegionManagementPage) {
          const { unassignedOrders, setUnassignedOrders } = regionStore;
          if (unassignedOrders && !isUnassignedAddress(fullAddress)) {
            setUnassignedOrders(false);
          }
        }

        // For table clicks, always center the map on the selection
        // (user is not looking at the map, so it makes sense to zoom to show what's selected)
        if (isShiftPressed) {
          // Bulk selection: compute what the new selection will be
          const currentSelection = store.selectedAddresses;
          const isAlreadySelected = currentSelection.some(
            (addr) => addr.id === fullAddress.id,
          );
          // Selection will either include or exclude the clicked address
          const newSelection = isAlreadySelected
            ? currentSelection.filter((addr) => addr.id !== fullAddress.id)
            : [...currentSelection, fullAddress];

          // Update selection
          setSelectedAddress([fullAddress], isShiftPressed);

          // Center on all selected addresses
          if (newSelection.length > 0) {
            setMapCentering(undefined, newSelection);
          }
        } else {
          // Single selection: update and center on just the clicked address
          setSelectedAddress([fullAddress], isShiftPressed);
          setMapCentering(undefined, [fullAddress]);
        }
      }
    },
    [
      findAddress,
      setMapCentering,
      setSelectedAddress,
      isRegionManagementPage,
      regionStore,
      store.selectedAddresses,
    ],
  );

  return { setClickedAddress };
};
